import React, { useEffect, useRef, useState } from 'react';
import { More } from '@wix/wix-ui-icons-common/on-stage';
import { IconButton, Popover } from '@wix/wix-ui-tpa/cssVars';
import ItemActionsMenu from './ItemActionsMenu/ItemActionsMenu';
import { setMenuSelectedItem } from '../../../../../../Services/MenuSelectedItem';
import { classes, st, vars } from './ItemActions.st.css';
import { vars as menuVars } from './ItemActionsMenu/ItemActionsMenu.st.css';
import { vars as moveVars } from '../../../../../../Shared/MoveWrapper/MoveWrapper.st.css';
import MoveWrapper from '../../../../../../Shared/MoveWrapper/MoveWrapper';
import useObservable from '../../../../../../../Shared/Hooks/useObservable.Hook';
import {
  getAppSettings,
  getSettingsData,
} from '../../../../../../Services/AppSettings.service';
import { TextAlignment } from '@wix/ambassador-file-sharing-v1-settings/types';

const ACTIONS_MENU_WIDTH = 240;
const MOVE_WRAPPER_WIDTH = 340;

// The custom-property names the portaled content depends on, taken from each
// stylesheet's `vars` export (a source-name -> runtime-name map). The runtime
// names are compressed at build time, so they must come from the stylesheets
// themselves rather than being derived from the style-param keys. Includes the
// menu and move stylesheets since their content is portaled too.
const PORTALED_STYLE_VARS = Array.from(
  new Set([
    ...Object.values(vars),
    ...Object.values(menuVars),
    ...Object.values(moveVars),
  ]),
);

// The Popover portals its content to <body> (appendTo="window") to escape the
// table's overflow/stacking context. That also escapes the Stylable var scope,
// so the widget's style-param CSS custom properties are no longer inherited by
// the portaled menu. We read their current values off the in-widget wrapper and
// re-apply them inline on the portaled content. Runs only on open (click-gated).
const getStyleParams = (element: HTMLElement | null): React.CSSProperties => {
  if (!element || typeof window === 'undefined') {
    return {};
  }

  const computedStyle = window.getComputedStyle(element);
  const styleParams: Record<string, string> = {};

  for (const varName of PORTALED_STYLE_VARS) {
    const value = computedStyle.getPropertyValue(varName).trim();

    if (value) {
      styleParams[varName] = value;
    }
  }

  return styleParams as React.CSSProperties;
};

const ItemActions = (props: any) => {
  const { item } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [showMenu, setShowMenu] = useState({
    showMenu: false,
    showMove: false,
  });
  const [popoverStyleParams, setPopoverStyleParams] =
    useState<React.CSSProperties>({});
  const [, AppSettings]: any = useObservable(getAppSettings());
  const [, settingsData]: any = useObservable(getSettingsData());
  const isMembersArea = AppSettings?.isMembersArea ?? false;
  const isRTL =
    settingsData?.settings?.displaySettings?.textAlignment ===
    TextAlignment.RIGHT;

  const handleCloseMenu = () => {
    setOpen(false);
    setShowMenu((prevState: any) => ({
      ...prevState,
      showMenu: false,
      showMove: false,
    }));
  };

  const handleOnClick = async () => {
    const nextOpen = !open;

    setPopoverStyleParams(getStyleParams(wrapperRef.current));
    setOpen(nextOpen);
    setShowMenu((prevState: any) => ({
      ...prevState,
      showMenu: nextOpen,
      showMove: false,
    }));
    await setMenuSelectedItem(item);
  };
  const handleOpenMove = () => {
    // setMoveLoader(true);
    setShowMenu((prevState: any) => ({
      ...prevState,
      showMenu: false,
      showMove: true,
    }));
  };
  const handleOnCloseMove = () => {
    setShowMenu((prevState: any) => ({
      ...prevState,
      showMenu: false,
      showMove: false,
    }));
  };

  // The Popover's onEscPress only fires while focus is inside the portaled
  // content. Since opening the menu leaves focus on the trigger/body, we also
  // listen on the document so Escape closes the menu regardless of focus.
  useEffect(() => {
    if (!open || (!showMenu.showMenu && !showMenu.showMove)) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, showMenu.showMenu, showMenu.showMove]);

  return (
    <div ref={wrapperRef}>
      <Popover
        key={showMenu.showMove ? 'move' : 'menu'}
        appendTo="window"
        className={st(classes.actionsMenuPopover, { isMembersArea })}
        contentClassName={st(classes.actionsMenuPopoverContent)}
        style={popoverStyleParams}
        shown={open && (showMenu.showMenu || showMenu.showMove)}
        placement={isRTL ? 'bottom-start' : 'bottom-end'}
        width={showMenu.showMove ? MOVE_WRAPPER_WIDTH : ACTIONS_MENU_WIDTH}
        showArrow={false}
        flip={!showMenu.showMove}
        onClickOutside={handleCloseMenu}
        onEscPress={handleCloseMenu}
      >
        <Popover.Element>
          <IconButton
            aria-label="more actions"
            type="button"
            aria-haspopup="menu"
            aria-expanded={open ? open && showMenu.showMenu : false}
            className={st(classes.iconButton)}
            icon={<More />}
            onClick={handleOnClick}
          />
        </Popover.Element>
        <Popover.Content>
          {showMenu.showMenu && (
            <ItemActionsMenu
              item={item}
              setOpen={setOpen}
              handleOpenMove={handleOpenMove}
              styleParams={popoverStyleParams}
            />
          )}
          {showMenu.showMove && (
            <MoveWrapper
              renderAsContent={true}
              handleOnCloseMove={handleOnCloseMove}
              styleParams={popoverStyleParams}
            />
          )}
        </Popover.Content>
      </Popover>
    </div>
  );
};

export default ItemActions;
